import Button from '@/component/Button'
import Form, { Field } from '@/component/FieldForm'
import Toast from '@/component/Toast'
import TGASDK from '@/config/TGASDK'
import AuthContext from '@/context/AuthContext'
import useRuntime from '@/hook/useRuntime'
import { PopUpStore } from '@/store/PopupStore'
import { RESPONSE_CODES } from '@kulee/tga-constant'
import { UC } from '@kulee/tga-sdk'
import { useMutation } from '@tanstack/react-query'
import React, { useContext, useEffect } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import EmailInput from '../../component/EmailInput'
import PasswordInput from '../../component/PasswordInput'
import VerificationCodeInput from '../../component/VerificationCodeInput'

const RegisterMain: React.FC<PropType> = () => {
  const { t } = useTranslation('uc')
  const { query } = useRuntime()
  const { init } = useContext(AuthContext)

  const mutation = useMutation({
    mutationFn: (values: FormValuesType) => {
      const { email, password } = values

      return TGASDK.module.UC.registerByAccountBindEmail({
        email,
        password,
        inviteCode: query.get('inviteCode') || undefined,
        code: '951753'
      })
    }
  })

  const handleRegister = async (values: FormValuesType) => {
    mutation.mutate(values, {
      onSuccess: () => {
        init()
        PopUpStore.setRegisterPopupVisible(false)
        Toast.show({
          content: t('message.registerSuccess'),
          icon: 'success'
        })
      },
      onError: (error: any) => {
        Toast.show({
          content:
            error.code === RESPONSE_CODES.UC_USER_EXIST
              ? t('message.registerAccountExistsFail')
              : t('message.registerFail'),
          icon: 'error'
        })
      }
    })
  }

  const handleLogin = () => {
    PopUpStore.setRegisterPopupVisible(false)
    PopUpStore.setLoginPopupVisible(true)
  }

  useEffect(() => {
    TGASDK.addModule(UC)
  }, [])

  return (
    <div className="flex flex-col gap-md">
      <div className="text-lg font-bold text-center">{t('tip.registerWelcome')}</div>
      <div>
        <Form onFinish={handleRegister}>
          {(values, form) => {
            const emailError = form.getFieldError('email')
            const codeError = form.getFieldError('code')
            const passwordError = form.getFieldError('password')
            const passwordComfirmError = form.getFieldError('passwordComfirm')

            return (
              <div className="flex flex-col gap-sm">
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="email"
                    validateDebounce={500}
                    validateFirst={true}
                    rules={[
                      {
                        required: true,
                        pattern: /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
                        message: t('message.emailFormatError')
                      },
                      {
                        validator: async (_: any, value: string) => {
                          const { data } = await TGASDK.module.UC.service.checkAccountExist(value)
                          if (data) {
                            return Promise.reject(t('message.accountExistsError'))
                          }
                          return Promise.resolve()
                        }
                      }
                    ]}
                  >
                    <EmailInput placeholder={t('placeholder.email')} />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{emailError[0]}</p>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="code"
                    rules={[
                      {
                        required: true,
                        pattern: /^\d{6}$/,
                        message: t('message.codeFormatError')
                      }
                    ]}
                  >
                    <VerificationCodeInput
                      placeholder={t('placeholder.code')}
                      email={emailError.length === 0 && values.email ? values.email : undefined}
                      grantType="REGISTER"
                    />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{codeError[0]}</p>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="password"
                    rules={[
                      {
                        required: true,
                        pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*?&]{6,}$/,
                        message: t('message.passwordFormatError')
                      }
                    ]}
                  >
                    <PasswordInput />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{passwordError[0]}</p>
                </div>
                <div className="flex flex-col gap-[2px]">
                  <Field<FormValuesType>
                    name="passwordComfirm"
                    rules={[
                      context => ({
                        validator(rule, value) {
                          const { password } = context.getFieldsValue(true)
                          return password === value
                            ? Promise.resolve()
                            : Promise.reject(t('message.passwordConfirmError'))
                        }
                      })
                    ]}
                  >
                    <PasswordInput placeholder={t('placeholder.passwordConfirm')} />
                  </Field>
                  <p className="h-4 overflow-hidden text-semantic-error px-sm">{passwordComfirmError[0]}</p>
                </div>
                <div className="mt-md">
                  <Button htmlType="submit" type="primary" className="w-full" loading={mutation.isPending}>
                    {t('btn.register')}
                  </Button>
                </div>
              </div>
            )
          }}
        </Form>
      </div>
      <div className="text-center">
        <Trans
          i18nKey="tip.accountExists"
          ns="uc"
          components={[<Button type="link" className="font-normal underline" onClick={handleLogin} />]}
        />
      </div>
      <div className="border-t border-gray-300 text-gray-500 pt-md">
        <Trans
          i18nKey="tip.agreement"
          ns="uc"
          components={[<a href="#" className="underline" />, <a href="#" className="underline" />]}
        />
      </div>
    </div>
  )
}

interface PropType {}

interface FormValuesType {
  email: string
  code: string
  password: string
  passwordComfirm: string
}

export default RegisterMain
